import React, { useCallback, useEffect, useMemo,useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AppWrapper, Spinner } from 'components';
import AffiliateBusinessModal from 'components/AffiliateBusinessModal/AffiliateBusinessModal';
import AffiliateCouponsList from 'components/AffiliateCouponsList/AffiliateCouponsList';
import AffiliateCustomCoupon from 'components/AffiliateCustomCoupon/AffiliateCustomCoupon';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import Pagination from 'components/Pagination/Pagination';
import ProductsStatus from 'components/ProductsStatus/ProductsStatus';
import { AFFILIATE_TYPES,COUPON_EXPIRY_TYPES, COUPON_STATES, COUPON_TYPES } from 'coupon-common';
import { actions as affiliateActions } from 'coupon-common/src/modules/affiliateBusiness';
import { actions as cjActions } from 'coupon-common/src/modules/cj';

// ─── Debug logger ────────────────────────────────────────────────────────────
const DEBUG = false;
const log = (label, data) => {
  if (!DEBUG) return;
  const ts = new Date().toISOString();
  data !== undefined
    ? console.log(`[CJCouponList][${ts}] ${label}`, data)
    : console.log(`[CJCouponList][${ts}] ${label}`);
};
const logWarn = (label, data) => {
  if (!DEBUG) return;
  console.warn(`[CJCouponList][${new Date().toISOString()}] ⚠️  ${label}`, data ?? '');
};

// ─── Discount parser (used for deals) ────────────────────────────────────────
const parseDiscount = (text) => {
  if (!text) return null;
  const t = text.toLowerCase();
  if (/bogo|buy\s+one\s+get\s+one/.test(t)) {
    return { type: COUPON_TYPES.BOGO, percentage_off: null, amount_off: null };
  }
  const pctMatch = t.match(/(\d+(?:\.\d+)?)\s*(?:%|percent)\s*off/);
  if (pctMatch) {
    return { type: COUPON_TYPES.PERCENTAGE_OFF, percentage_off: parseFloat(pctMatch[1]), amount_off: null };
  }
  const amtMatch = t.match(/(?:save\s+)?\$\s*(\d+(?:\.\d+)?)\s*off|(\d+(?:\.\d+)?)\s*\$\s*off/);
  if (amtMatch) {
    return { type: COUPON_TYPES.AMOUNT_OFF, percentage_off: null, amount_off: parseFloat(amtMatch[1] || amtMatch[2]) };
  }
  return null;
};

// ─── Sale product helpers ─────────────────────────────────────────────────────
const calculateSavings = (price, salePrice) => {
  if (!price || !salePrice || price <= 0) return 0;
  return Math.round(((price - salePrice) / price) * 100);
};

// ─── Filter defaults ──────────────────────────────────────────────────────────
const DISCOUNT_PRESETS = [10, 20, 30, 40, 50];

const DEFAULT_FILTERS = {
  minDiscount: 0,       // % — 0 means no minimum
  customDiscount: '',   // user-typed custom minimum %
  startDate: '',        // ISO date string yyyy-mm-dd
  endDate: '',          // ISO date string yyyy-mm-dd
  titleSearch: '',      // free text
};

// Modes: 'deals' | 'products'
const MODE_DEALS = 'deals';
const MODE_PRODUCTS = 'products';

const CJCouponList = () => {
  const dispatch = useDispatch();
  const AFFILIATE_TYPE = AFFILIATE_TYPES.cj;

  // ── Selectors ──────────────────────────────────────────────────────────────
  const allDeals = useSelector((state) => {
    const val = state.cj?.deals || [];
    log('selector:cj.deals', val);
    return val;
  });

  const advertisers = useSelector((state) => {
    const val = state.cj?.advertisers || [];
    log('selector:cj.advertisers', val);
    return val;
  });

  const productImages = useSelector((state) => {
    const val = state.cj?.productImages || [];
    log('selector:cj.productImages', val);
    return val;
  });

  const saleProducts = useSelector((state) => {
    const val = state.cj?.saleProducts || [];
    log('selector:cj.saleProducts', val);
    return val;
  });

  const cjLoading = useSelector((state) => {
    const val = state.cj?.loading;
    log('selector:cj.loading', val);
    return val;
  });

  const affiliateBusinesses = useSelector((state) => state.affiliateBusiness?.data?.results || []);

  // ── Local state ────────────────────────────────────────────────────────────
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState('');
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [fetchMode, setFetchMode] = useState(null);
  const [dataRequested, setDataRequested] = useState(false);

  // Raw unfiltered products list (set once when saleProducts arrive)
  const [rawProducts, setRawProducts] = useState([]);

  // Filtered + sorted list (driven by filters)
  const [filteredItems, setFilteredItems] = useState([]);
  const [mappedCoupons, setMappedCoupons] = useState([]);
  const [couponImages, setCouponImages] = useState({});

  // ── Product filters (only active in MODE_PRODUCTS) ────────────────────────
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [currentPage, setCurrentPage] = useState(1);
  const [firstItemIndex, setFirstItemIndex] = useState(1);
  const [lastItemIndex, setLastItemIndex] = useState(10);
  const postsPerPage = 10;
  const [showCustomCoupon, setShowCustomCoupon] = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalBusiness, setModalBusiness] = useState(null);

  // ── Mount ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    log('mount — fetching CJ advertisers and affiliate businesses');
    dispatch(cjActions.getCJAdvertisers());
    dispatch(affiliateActions.getAffiliateBusinesses());
  }, [dispatch]);

  // ── Populate filteredItems when deals arrive ───────────────────────────────
  useEffect(() => {
    if (!dataRequested || fetchMode !== MODE_DEALS) return;
    log('allDeals updated after deals request', { count: allDeals.length });
    setFilteredItems(allDeals);
    setCurrentPage(1);
  }, [allDeals, dataRequested, fetchMode]);

  // ── Populate rawProducts when sale products arrive ────────────────────────
  useEffect(() => {
    if (!dataRequested || fetchMode !== MODE_PRODUCTS) return;
    log('saleProducts updated after products request', { count: saleProducts.length });

    const withDiscount = saleProducts.filter((p) => {
      const price = parseFloat(p.price?.amount);
      const sale = parseFloat(p.salePrice?.amount);
      return !isNaN(price) && !isNaN(sale) && sale < price;
    });

    const sorted = [...withDiscount].sort((a, b) => {
      const savA = calculateSavings(parseFloat(a.price?.amount), parseFloat(a.salePrice?.amount));
      const savB = calculateSavings(parseFloat(b.price?.amount), parseFloat(b.salePrice?.amount));
      return savB - savA;
    });

    log('rawProducts set', { total: saleProducts.length, withDiscount: sorted.length });
    setRawProducts(sorted);
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, [saleProducts, dataRequested, fetchMode]);

  // ── Apply filters to rawProducts → filteredItems ──────────────────────────
  useEffect(() => {
    if (fetchMode !== MODE_PRODUCTS) return;

    const minPct = filters.customDiscount !== ''
      ? parseFloat(filters.customDiscount) || 0
      : filters.minDiscount;

    const startTs = filters.startDate ? new Date(filters.startDate).getTime() : null;
    const endTs   = filters.endDate   ? new Date(filters.endDate).getTime()   : null;
    const search  = filters.titleSearch.trim().toLowerCase();

    const result = rawProducts.filter((p) => {
      const price    = parseFloat(p.price?.amount);
      const sale     = parseFloat(p.salePrice?.amount);
      const discount = calculateSavings(price, sale);

      if (minPct > 0 && discount < minPct) return false;

      if (startTs && p.salePriceEffectiveDateStart) {
        if (new Date(p.salePriceEffectiveDateStart).getTime() < startTs) return false;
      }
      if (endTs && p.salePriceEffectiveDateEnd) {
        if (new Date(p.salePriceEffectiveDateEnd).getTime() > endTs) return false;
      }

      if (search && !(p.title || '').toLowerCase().includes(search)) return false;

      return true;
    });

    log('filters applied', { minPct, startTs, endTs, search, before: rawProducts.length, after: result.length });
    setFilteredItems(result);
    setCurrentPage(1);
  }, [filters, rawProducts, fetchMode]);

  // ── Assign random images — deals mode only ────────────────────────────────
  useEffect(() => {
    if (!productImages.length || !filteredItems.length) return;
    if (fetchMode === MODE_PRODUCTS) return; // products carry their own imageLink
    log('productImages arrived — assigning random images', { imageCount: productImages.length });
    const assignments = {};
    filteredItems.forEach((item, i) => {
      const localId = `cj-${item.link_id}-${i}`;
      const randomImg = productImages[Math.floor(Math.random() * productImages.length)];
      assignments[localId] = randomImg?.image_url || '';
    });
    setCouponImages(assignments);
  }, [productImages, filteredItems, fetchMode]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const selectedBusinessInDb = affiliateBusinesses.find(
    b => String(b.affiliate_code) === String(selectedAdvertiserId)
  ) || null;

  const selectedAdvertiserInDb = selectedAdvertiser
    ? affiliateBusinesses.some(b => String(b.affiliate_code) === String(selectedAdvertiser.advertiser_id))
    : false;

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleSetMinDiscount = (pct) => {
    setFilters(f => ({ ...f, minDiscount: pct, customDiscount: '' }));
  };

  const handleCustomDiscountChange = (e) => {
    setFilters(f => ({ ...f, customDiscount: e.target.value, minDiscount: 0 }));
  };

  const handleFilterChange = (field) => (e) => {
    setFilters(f => ({ ...f, [field]: e.target.value }));
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    const minPct = filters.customDiscount !== '' ? parseFloat(filters.customDiscount) || 0 : filters.minDiscount;
    if (minPct > 0) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.titleSearch.trim()) count++;
    return count;
  }, [filters]);

  // ── Map deal → coupon ──────────────────────────────────────────────────────
  const mapDealToCoupon = useCallback((deal, index) => {
    const defaultCategory = selectedBusinessInDb?.categories?.[0] ?? null;
    const parsed = parseDiscount(deal.description) || parseDiscount(deal.link_name);
    const now = new Date();
    const endDate = deal.promotion_end_date
      ? new Date(deal.promotion_end_date)
      : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const localId = `cj-${deal.link_id}-${index}`;

    return {
      localId,
      title: deal.link_name?.length > 35 ? deal.link_name.slice(0, 32) + '...' : deal.link_name || '',
      description: deal.description || '',
      coupon_code: deal.coupon_code || '',
      type: parsed?.type ?? COUPON_TYPES.PERCENTAGE_OFF,
      percentage_off: parsed?.percentage_off ?? null,
      amount_off: parsed?.amount_off ?? null,
      affiliate_link: deal.click_url || '',
      link_url: deal.click_url || '',
      published_at: deal.promotion_start_date ? new Date(deal.promotion_start_date) : now,
      expiration_date: endDate,
      expiry_type: COUPON_EXPIRY_TYPES.DATE,
      state: COUPON_STATES.PUBLISHED,
      auto_accept: true,
      is_affiliate_or_flyer: true,
      advertiser_id: deal.advertiser_id,
      advertiser_name: deal.advertiser_name,
      category: defaultCategory,
      _cjCategory: deal.category,
      sale_commission: deal.sale_commission,
      targeted_countries: deal.targeted_countries,
      _editableDiscount: parsed,
      _rawDeal: deal,
    };
  }, [selectedBusinessInDb]);

  // ── Map sale product → coupon ─────────────────────────────────────────────
  const mapProductToCoupon = useCallback((product, index) => {
    const defaultCategory = selectedBusinessInDb?.categories?.[0] ?? null;
    const price = parseFloat(product.price?.amount);
    const salePrice = parseFloat(product.salePrice?.amount);
    const currency = product.price?.currency || 'USD';
    const discountPercent = calculateSavings(price, salePrice);
    const amountOff = !isNaN(price) && !isNaN(salePrice) ? parseFloat((price - salePrice).toFixed(2)) : null;

    let type, percentage_off, amount_off;
    if (discountPercent >= 10) {
      type = COUPON_TYPES.PERCENTAGE_OFF;
      percentage_off = discountPercent;
      amount_off = null;
    } else {
      type = COUPON_TYPES.AMOUNT_OFF;
      percentage_off = null;
      amount_off = amountOff;
    }

    const now = new Date();
    const endDate = product.salePriceEffectiveDateEnd
      ? new Date(product.salePriceEffectiveDateEnd)
      : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    const localId = `cj-product-${product.id}-${index}`;
    const imageUrl = product.imageLink || productImages[Math.floor(Math.random() * productImages.length)]?.image_url || '';
    const title = product.title || '';

    return {
      localId,
      title: title.length > 35 ? title.slice(0, 32) + '...' : title,
      description: product.description || '',
      price: !isNaN(price) ? price.toFixed(2) : '',
      sale_price: !isNaN(salePrice) ? salePrice.toFixed(2) : '',
      currency,
      coupon_code: '',
      type,
      percentage_off,
      amount_off,
      affiliate_link: product.linkCode?.clickUrl || product.link || '',
      link_url: product.linkCode?.clickUrl || product.link || '',
      published_at: product.salePriceEffectiveDateStart ? new Date(product.salePriceEffectiveDateStart) : now,
      expiration_date: endDate,
      expiry_type: COUPON_EXPIRY_TYPES.DATE,
      state: COUPON_STATES.PUBLISHED,
      auto_accept: true,
      is_affiliate_or_flyer: true,
      advertiser_id: product.advertiserId,
      advertiser_name: product.advertiserName,
      category: defaultCategory,
      image_url: imageUrl ? [{ image_url: imageUrl, sequence: 1 }] : [],
      _brand: product.brand || '',
      _googleCategory: product.googleProductCategory?.name || '',
      _rawProduct: product,
    };
  }, [selectedBusinessInDb, productImages]);

  // ── Pagination slice ───────────────────────────────────────────────────────
  useEffect(() => {
    const lastIdx = currentPage * postsPerPage;
    const firstIdx = lastIdx - postsPerPage;
    setFirstItemIndex(firstIdx + 1);
    setLastItemIndex(lastIdx);

    const slice = filteredItems.slice(firstIdx, lastIdx).map((item, i) =>
      fetchMode === MODE_PRODUCTS
        ? mapProductToCoupon(item, i)
        : mapDealToCoupon(item, i)
    );

    log('pagination slice', { currentPage, firstIdx, lastIdx, sliceCount: slice.length, fetchMode });
    setMappedCoupons(slice);
  }, [currentPage, filteredItems, mapDealToCoupon, mapProductToCoupon, fetchMode]);

  const buildBusinessData = (advertiser) => ({
    name: advertiser.advertiser_name,
    logo: '',
    cover_image: '',
    business_web_url: advertiser.program_url || '',
    terms_and_conditions: '',
    affiliate_code: advertiser.advertiser_id,
    affiliate_type: AFFILIATE_TYPES.cj,
    is_affiliate: true,
    is_flyer: false,
    categories: [],
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdvertiserChange = (e) => {
    const id = e.target.value;
    log('handleAdvertiserChange', id);
    setSelectedAdvertiserId(id);
    setSelectedAdvertiser(advertisers.find((a) => a.advertiser_id === id) || null);
    setDataRequested(false);
    setFetchMode(null);
    setRawProducts([]);
    setFilteredItems([]);
    setMappedCoupons([]);
    setCouponImages({});
    setFilters(DEFAULT_FILTERS);
    setShowCustomCoupon(false);
  };

  const handleFetchDeals = () => {
    if (!selectedAdvertiserId) {
      logWarn('handleFetchDeals — no advertiser selected');
      return;
    }
    log('handleFetchDeals', { selectedAdvertiserId });
    setShowCustomCoupon(false);
    setDataRequested(true);
    setFetchMode(MODE_DEALS);
    setRawProducts([]);
    setFilteredItems([]);
    setMappedCoupons([]);
    setFilters(DEFAULT_FILTERS);
    dispatch(cjActions.getCJDeals({ advertiser_id: selectedAdvertiserId, discount_only: 'true' }));
    dispatch(cjActions.getCJProductImages({ advertiser_id: selectedAdvertiserId }));
  };

  const handleFetchSaleProducts = () => {
    if (!selectedAdvertiserId) {
      logWarn('handleFetchSaleProducts — no advertiser selected');
      return;
    }
    log('handleFetchSaleProducts', { selectedAdvertiserId });
    setShowCustomCoupon(false);
    setDataRequested(true);
    setFetchMode(MODE_PRODUCTS);
    setRawProducts([]);
    setFilteredItems([]);
    setMappedCoupons([]);
    setFilters(DEFAULT_FILTERS);
    dispatch(cjActions.getCJSaleProducts({ partner_id: selectedAdvertiserId }));
    dispatch(cjActions.getCJProductImages({ advertiser_id: selectedAdvertiserId }));
  };

  const handleRefresh = () => {
    if (!selectedAdvertiserId || !fetchMode) return;
    log('handleRefresh', { fetchMode });
    setShowCustomCoupon(false);
    setDataRequested(true);
    setRawProducts([]);
    setFilteredItems([]);
    setMappedCoupons([]);
    if (fetchMode === MODE_PRODUCTS) {
      dispatch(cjActions.getCJSaleProducts({ partner_id: selectedAdvertiserId }));
      dispatch(cjActions.getCJProductImages({ advertiser_id: selectedAdvertiserId }));
    } else {
      dispatch(cjActions.getCJDeals({ advertiser_id: selectedAdvertiserId, discount_only: 'true' }));
      dispatch(cjActions.getCJProductImages({ advertiser_id: selectedAdvertiserId }));
    }
  };

  const handleReset = () => {
    // log('handleRemove', localId);
    if (showCustomCoupon) {
      setShowCustomCoupon(false);
      setDataRequested(false);
    }
  };

  const handleImageChange = (localId, newImageUrl) => {
    log('handleImageChange', { localId, newImageUrl });
    setCouponImages((prev) => ({ ...prev, [localId]: newImageUrl }));
  };

  const handleAddNewBusiness = () => {
    setModalBusiness(null);
    setModalOpen(true);
  };

  const handleAddThisAdvertiser = () => {
    if (!selectedAdvertiser) return;
    setModalBusiness(buildBusinessData(selectedAdvertiser));
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalBusiness(null);
    dispatch(affiliateActions.getAffiliateBusinesses());
  };

  const handleCustomCoupon = () => {
    setRawProducts([]);
    setFilteredItems([]);
    setMappedCoupons([]);
    setShowCustomCoupon(true);
    setDataRequested(true);
  };

  // ── Build coupons with current image selections ────────────────────────────
  const couponsWithImages = mappedCoupons.map((coupon) => ({
    ...coupon,
    image_url: couponImages[coupon.localId]
      ? [{ image_url: couponImages[coupon.localId], sequence: 1 }]
      : coupon.image_url || [],
  }));

  const advertiserSelected = !!selectedAdvertiserId;
  const imagesLoading = cjLoading?.productImages;
  const dealsLoading = cjLoading?.deals;
  const productsLoading = cjLoading?.saleProducts;
  const activeLoading = fetchMode === MODE_PRODUCTS ? productsLoading : dealsLoading;

  const statusTitle = fetchMode === MODE_PRODUCTS
    ? '🛍️ CJ Sale Products'
    : '🏷️ CJ Deals with Discounts';

  // Active min discount for button highlight
  const activeMinDiscount = filters.customDiscount !== ''
    ? parseFloat(filters.customDiscount) || 0
    : filters.minDiscount;

  return (
    <AppWrapper showHeader showSideNav showHeadingRow headingText="🏷️ CJ Affiliate Coupon Tool">
      <div className="col-md-12">
        <Breadcrumbs locationArray={['Admin', 'CJ Import']} />

        {/* ── Controls bar ── */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-end g-3">

              {/* Advertiser dropdown */}
              <div className="col-md-5">
                <label className="form-label fw-semibold">Select Advertiser</label>
                <select
                  className="form-select"
                  value={selectedAdvertiserId}
                  onChange={handleAdvertiserChange}
                  disabled={cjLoading?.advertisers}
                >
                  <option value="">— Choose an advertiser —</option>
                  {advertisers.map((adv) => (
                    <option key={adv.advertiser_id} value={adv.advertiser_id}>
                      {adv.advertiser_name}
                      {affiliateBusinesses.some(b => String(b.affiliate_code) === String(adv.advertiser_id))
                        ? ' ✓'
                        : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Advertiser meta + DB status */}
              {selectedAdvertiser && (
                <div className="col-md-4">
                  <small className="text-muted d-block">
                    <strong>Category:</strong> {selectedAdvertiser.category || '—'}
                  </small>
                  <small className="text-muted d-block">
                    <strong>Commission:</strong> {selectedAdvertiser.actions?.[0]?.commission || '—'}
                  </small>
                  <small className="text-muted d-block">
                    <strong>7-day EPC:</strong> {selectedAdvertiser.seven_day_epc || '—'}
                  </small>
                  {selectedAdvertiserInDb ? (
                    <small className="text-success d-block mt-1">✓ In your database</small>
                  ) : (
                    <small className="text-warning d-block mt-1">⚠ Not yet in your database</small>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="col-md-3 text-end d-flex flex-column gap-2 align-items-end">
                <button
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={handleAddNewBusiness}
                >
                  + Add New Business
                </button>

                {advertiserSelected && !selectedAdvertiserInDb && (
                  <button
                    className="btn btn-warning btn-sm w-100"
                    onClick={handleAddThisAdvertiser}
                  >
                    + Add This Advertiser
                  </button>
                )}

                {/* ── Fetch row: Deals | Sale Products | Refresh ── */}
                <div className="d-flex gap-2 w-100">
                  <button
                    className={`btn btn-sm flex-grow-1 ${fetchMode === MODE_DEALS && dataRequested ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={handleFetchDeals}
                    disabled={!advertiserSelected || dealsLoading}
                    title="Fetch promotional links (coupons, discounts) from CJ Link Search"
                  >
                    {dealsLoading && fetchMode === MODE_DEALS ? <Spinner small /> : '🏷️ Deals'}
                  </button>

                  <button
                    className={`btn btn-sm flex-grow-1 ${fetchMode === MODE_PRODUCTS && dataRequested ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={handleFetchSaleProducts}
                    disabled={!advertiserSelected || productsLoading}
                    title="Fetch sale-priced products from CJ Product Feed API"
                  >
                    {productsLoading && fetchMode === MODE_PRODUCTS ? <Spinner small /> : '🛍️ Products'}
                  </button>

                  {dataRequested && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleRefresh}
                      disabled={activeLoading}
                      title="Refresh"
                    >
                      🔄
                    </button>
                  )}
                </div>

                {advertiserSelected && selectedAdvertiserInDb && (
                  <button
                    className="btn btn-outline-primary btn-sm w-100"
                    onClick={handleCustomCoupon}
                  >
                    Custom Coupon +
                  </button>
                )}
              </div>
            </div>

            {!advertiserSelected && !cjLoading?.advertisers && (
              <p className="text-muted mt-3 mb-0">
                ☝️ Select an advertiser above then click <strong>🏷️ Deals</strong> for promotional links
                or <strong>🛍️ Products</strong> for sale-priced items from the Product Feed API.
              </p>
            )}

            {/* Mode indicator */}
            {dataRequested && !showCustomCoupon && fetchMode && (
              <div className="mt-2">
                <small className={`badge ${fetchMode === MODE_PRODUCTS ? 'bg-success' : 'bg-primary'}`}>
                  {fetchMode === MODE_PRODUCTS ? '🛍️ Product Feed mode' : '🏷️ Deals mode'}
                </small>
              </div>
            )}
          </div>
        </div>

        {/* ── Product filters (products mode only) ─────────────────────────── */}
        {fetchMode === MODE_PRODUCTS && rawProducts.length > 0 && !showCustomCoupon && (
          <div className="card mb-3 border-success">
            <div className="card-header bg-success bg-opacity-10 d-flex justify-content-between align-items-center py-2">
              <span className="fw-semibold text-success">
                🔍 Filter Products
                {activeFilterCount > 0 && (
                  <span className="badge bg-success ms-2">{activeFilterCount} active</span>
                )}
              </span>
              <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                  {filteredItems.length} of {rawProducts.length} products
                </small>
                {activeFilterCount > 0 && (
                  <button
                    className="btn btn-sm btn-outline-secondary py-0"
                    onClick={handleClearFilters}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            <div className="card-body py-3">
              <div className="row g-3">

                {/* ── Discount % filter ── */}
                <div className="col-md-5">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.8rem' }}>
                    Min. Discount
                  </label>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {DISCOUNT_PRESETS.map((pct) => (
                      <button
                        key={pct}
                        className={`btn btn-sm ${activeMinDiscount === pct && filters.customDiscount === '' ? 'btn-success' : 'btn-outline-success'}`}
                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                        onClick={() => handleSetMinDiscount(pct)}
                      >
                        &gt;{pct}%
                      </button>
                    ))}
                    <button
                      className={`btn btn-sm ${filters.minDiscount === 0 && filters.customDiscount === '' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                      onClick={() => handleSetMinDiscount(0)}
                    >
                      Any
                    </button>
                  </div>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">&gt;</span>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Custom %"
                      min="1"
                      max="99"
                      value={filters.customDiscount}
                      onChange={handleCustomDiscountChange}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>

                {/* ── Date filters ──
                <div className="col-md-4">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.8rem' }}>
                    Sale Date Range
                  </label>
                  <div className="d-flex flex-column gap-2">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text" style={{ fontSize: '0.75rem' }}>From</span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.startDate}
                        onChange={handleFilterChange('startDate')}
                      />
                    </div>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text" style={{ fontSize: '0.75rem' }}>To</span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.endDate}
                        onChange={handleFilterChange('endDate')}
                      />
                    </div>
                  </div>
                </div> */}

                {/* ── Title search ── */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.8rem' }}>
                    Search Title
                  </label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">🔍</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. desk, chair..."
                      value={filters.titleSearch}
                      onChange={handleFilterChange('titleSearch')}
                    />
                    {filters.titleSearch && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setFilters(f => ({ ...f, titleSearch: '' }))}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* ── Image pool status ── */}
        {dataRequested && !showCustomCoupon && (
          <div className="mb-2">
            {imagesLoading ? (
              <small className="text-muted">🖼️ Loading product images...</small>
            ) : productImages.length > 0 ? (
              <small className="text-success">
                🖼️ {productImages.length} product images loaded — click <strong>Choose image</strong> on any coupon to pick a different one
              </small>
            ) : (
              <small className="text-muted">🖼️ No product images available for this advertiser</small>
            )}
          </div>
        )}

        {/* ── Results ── */}
        {dataRequested && (
          <>
            {showCustomCoupon ? (
              <AffiliateCustomCoupon
                affiliateBusinesses={affiliateBusinesses}
                selectedBusinessId={selectedBusinessInDb?.id || null}
                dispatch={dispatch}
                inputValue={String(selectedAdvertiserId)}
                onClose={handleReset}
              />
            ) : (
              <>
                <ProductsStatus
                  title={statusTitle}
                  items={filteredItems}
                  firstIndex={firstItemIndex}
                  lastIndex={lastItemIndex}
                  loading={activeLoading}
                  displayedItems={couponsWithImages}
                  productsRequested={dataRequested}
                />

                <Pagination
                  totalPosts={filteredItems.length}
                  postsPerPage={postsPerPage}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                />

                <AffiliateCouponsList
                  coupons={couponsWithImages}
                  loading={activeLoading}
                  affiliateBusinesses={affiliateBusinesses}
                  selectedBusinessId={selectedBusinessInDb?.id || null}
                  inputValue={String(selectedAdvertiserId)}
                  dispatch={dispatch}
                  // handleRemove={handleRemove}
                  selectedBusiness={selectedBusinessInDb}
                  productImages={productImages}
                  onImageChange={handleImageChange}
                  defaultCategory={selectedBusinessInDb?.categories?.[0] || null}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* ── Business modal ── */}
      <AffiliateBusinessModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        business={modalBusiness}
        defaultAffiliateType={AFFILIATE_TYPES.cj}
      />
    </AppWrapper>
  );
};

export default CJCouponList;